/**
 * The CDN keys for an exercise clip, derived from its content hash exactly as
 * the catalogue ingest wrote them (`exerciseClip` in `@repo/contract`, D121).
 *
 * ── NEVER A CACHE-BUSTING QUERY STRING ─────────────────────────────────────
 * `posters/<hash>.webp` is served `public, max-age=31536000, immutable`. A
 * `?v=` would not refresh anyone's cached copy, it would only split the cache
 * and download every poster twice. A re-framed poster gets a NEW key (a
 * `posters/v2/` prefix and the column repointed), never a new query.
 */
const CDN = "https://cdn.mypocketgym.app";

/** The still frame, `posters/<hash>.webp`. */
export function poster(hash: string): string {
  return `${CDN}/posters/${hash}.webp`;
}

/** The ~6 s silent loop, `clips/<hash>.mp4`. */
export function clip(hash: string): string {
  return `${CDN}/clips/${hash}.mp4`;
}

/**
 * The exercise's figure on a transparent background, `cutouts/<hash>.webp`
 * (`D196`) — `cutoutHash` on a list row or a detail. Content-addressed and
 * immutable like a poster, so the same no-query-string rule holds.
 */
export function cutoutUrl(hash: string): string {
  return `${CDN}/cutouts/${hash}.webp`;
}

/**
 * The start and finish poses side by side, `frames/<hash>.webp` (`D196`) —
 * the detail's `framesHash`. Immutable like the rest of the CDN.
 */
export function framesUrl(hash: string): string {
  return `${CDN}/frames/${hash}.webp`;
}

/**
 * One body-map layer, `anatomy/<name>.webp` — a name from a muscle detail's
 * `overlays`, or one of the base figures (`D196`).
 *
 * ── FROM THE APP'S OWN BUNDLE, NOT THE CDN ─────────────────────────────────
 * The layers live in `apps/mobile/public/anatomy/`, so they ship inside the
 * bundle and draw offline — on the native shell, where Capacitor serves the
 * bundle from `https://localhost`, and in live reload, where Vite serves
 * `public/` at the root. Resolved against Vite's `BASE_URL` rather than a bare
 * `/`, so a build under a sub-path still finds them. NOT the CDN on purpose:
 * a body map that needs the network is a blank figure in the gym's basement.
 */
export function anatomyLayerUrl(name: string): string {
  return `${import.meta.env.BASE_URL}anatomy/${name}.webp`;
}

let onDisk: (hash: string) => string | null = () => null;

/**
 * Where the device keeps clips it downloaded (`D194`): the app registers
 * `native/clip-cache.ts`'s lookup at boot, so this layer never imports the
 * native one. Unregistered — the web, a story — nothing is on disk.
 */
export function registerClipsOnDisk(
  lookup: (hash: string) => string | null
): void {
  onDisk = lookup;
}

/**
 * The clip's source as a player should load it: the disk copy when there is
 * one, else the CDN. Read ONCE, when the `<video>` mounts — never reassigned,
 * which would reload it (`T-155`).
 */
export function clipSrc(hash: string): string {
  return onDisk(hash) ?? clip(hash);
}
